import { put, takeLatest, call } from 'redux-saga/effects'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import UrlHelper from 'helpers/url'
import * as ApiRoute from 'constants/apiRouting'
import NoticeHelper from 'helpers/notice'
import { SHOW_NOTICE } from '../notice'
import {
  CREATE_OR_UPDATE_TRIGGER_RULE,
  FETCH_TRIGGER_EXISTS,
  FETCH_TRIGGER_INPUTS,
  FETCH_TRIGGER_INPUTS_FAILURE,
  FETCH_TRIGGER_INPUTS_SUCCESS,
  SET_IS_FETCHING,
  FAILURE_FETCH_TRIGGER_TABLE_DATA,
  FETCH_TRIGGER_TABLE_DATA,
  SET_TRIGGER_TABLE_IS_LOADING,
  SUCCESS_FETCH_TRIGGER_TABLE_DATA,
  DELETE_TRIGGER_RULE,
  TOGGLE_TRIGGER_RULE,
  DUPLICATE_TRIGGER_RULE,
  FETCH_TRIGGER_RULE,
  SET_FORM_DATA,
  FETCH_TRIGGER_DEFAULT_ASSIGNMENTS,
  SUCCESS_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS,
  FAILURE_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS,
  FETCH_HIERARCHICAL_SITE_GROUPS,
  SUCCESS_FETCH_HIERARCHICAL_SITE_GROUPS,
  FAILURE_FETCH_HIERARCHICAL_SITE_GROUPS,
  SET_HIERARCHICAL_SITE_GROUPS_LOADING,
} from './reducer'

function* fetchTriggerTableData({ payload: { psfs } }) {
  try {
    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: true })
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.TRIGGER_TABLE}${UrlHelper.getPsfsQuery({ psfs })}`,
    })

    if (response && !response.error) {
      yield put({ type: SUCCESS_FETCH_TRIGGER_TABLE_DATA, payload: response })
    } else {
      yield put({ type: FAILURE_FETCH_TRIGGER_TABLE_DATA })
    }

    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: false })
  } catch (error) {
    console.error('FETCH TRIGGER TABLE DATA FAILED:', error)
  }
}

function* fetchTriggerDefaultAssignments({
  payload: {
    triggeredJobWorkflowId,
    siteId,
    shouldSetDefaultAssignmentsToForm,
  },
  promise,
}) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.DEFAULT_ASSIGNMENTS, {
        triggeredJobWorkflowId,
        siteId,
      }),
    })

    if (response && !response.error) {
      yield put({
        type: SUCCESS_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS,
        payload: response,
      })

      if (shouldSetDefaultAssignmentsToForm && promise && promise.resolve) {
        yield call(promise.resolve, response)
      }
    } else {
      yield put({ type: FAILURE_FETCH_TRIGGER_DEFAULT_ASSIGNMENTS })
    }

    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: false })
  } catch (error) {
    console.error('FETCH TRIGGER TABLE DATA FAILED:', error)
  }
}

function* fetchTriggerRule({ payload: { id } }) {
  try {
    yield put({ type: SET_IS_FETCHING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.RULE_ACTION, { id }),
    })

    if (response && !response.errors) {
      const formattedResponse = {
        ...response,
        mismatches: undefined,
      }

      yield put({ type: SET_FORM_DATA, payload: formattedResponse })
    }

    yield put({ type: SET_IS_FETCHING, payload: false })
  } catch (error) {
    console.error('TOGGLE TRIGGER RULE FAILED:', error)
  }
}

function* deleteTriggerRule({ payload, promise }) {
  try {
    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.RULE_ACTION, { id: payload }),
      method: 'DELETE',
    })

    if (response && !response.error) {
      if (promise && promise.hasOwnProperty('resolve')) {
        yield call(promise.resolve, false)
      }
    }

    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: false })
  } catch (error) {
    console.error('DELETE TRIGGER RULE DATA FAILED:', error)
  }
}

function* toggleTriggerRule({
  payload: { id, active, successfulMessage, failedMessage },
  promise,
}) {
  try {
    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.RULE_ACTION, { id }),
      method: 'PATCH',
      data: { active },
    })

    if (response && !response.errors) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successfulMessage),
      })

      if (promise && promise.hasOwnProperty('resolve')) {
        yield call(promise.resolve, false)
      }
    } else {
      const message =
        (response.errors && response.errors.trigger_rule) || failedMessage

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(message),
      })
    }

    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: false })
  } catch (error) {
    console.error('TOGGLE TRIGGER RULE FAILED:', error)
  }
}

function* duplicateTriggerRule({
  payload: { id, successfulMessage, failedMessage },
  promise,
}) {
  try {
    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.DUPLICATE_RULE, { id }),
      method: 'PUT',
    })

    if (response && !response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successfulMessage),
      })

      if (promise && promise.hasOwnProperty('resolve')) {
        yield call(promise.resolve, false)
      }
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(failedMessage),
      })
    }

    yield put({ type: SET_TRIGGER_TABLE_IS_LOADING, payload: false })
  } catch (error) {
    console.error('DUPLICATE TRIGGER RULE FAILED:', error)
  }
}

function* fetchTriggerInputs({ payload: { id } }) {
  try {
    yield put({ type: SET_IS_FETCHING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.TRIGGER_INPUTS, { id }),
    })

    if (response && !response.hasOwnProperty('error')) {
      yield put({ type: FETCH_TRIGGER_INPUTS_SUCCESS, payload: response })
    } else {
      yield put({ type: FETCH_TRIGGER_INPUTS_FAILURE })
    }

    yield put({ type: SET_IS_FETCHING, payload: false })
  } catch (error) {
    console.log('FETCH TRIGGER INPUTS FAILURE', error)
  }
}

function* fetchTriggerExists({
  payload: { queryParams, warningMessage },
  promise,
}) {
  try {
    yield put({ type: SET_IS_FETCHING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.createQuery(ApiRoute.TRIGGER_EXISTS, queryParams),
    })

    if (
      response &&
      !response.hasOwnProperty('error') &&
      !response.exists &&
      promise &&
      promise.resolve
    ) {
      yield call(promise.resolve, response)
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createWarningObj(warningMessage),
      })
    }

    yield put({ type: SET_IS_FETCHING, payload: false })
  } catch (error) {
    console.log('FETCH TRIGGER EXISTS FAILURE', error)
  }
}

function* createOrUpdateTriggerRule({
  payload: { data, id, successMessage, errorMessage },
  promise,
}) {
  try {
    if (id) {
      yield put({ type: SET_IS_FETCHING, payload: true })
    }

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.TRIGGER_RULES}${id ? `/${id}` : ''}`,
      method: id ? 'PUT' : 'POST',
      data,
    })

    if (response && !response.hasOwnProperty('error')) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else {
      const errorsArr = Object.values(response.errors) || {}

      for (let i = 0; i < errorsArr.length; i += 1) {
        yield put({
          type: SHOW_NOTICE,
          notice: NoticeHelper.createErrorObj(errorsArr[i] || errorMessage),
        })
      }
    }

    if (promise && promise.resolve) {
      yield call(promise.resolve, response)
    }

    yield put({ type: SET_IS_FETCHING, payload: false })
  } catch (error) {
    console.log('FETCH CREATE OR UPDATE TRIGGER RULE FAILURE', error)
  }
}

function* fetchHierarchicalSiteGroups() {
  try {
    yield put({ type: SET_HIERARCHICAL_SITE_GROUPS_LOADING, payload: true })

    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRoute.SITE_GROUPS_HIERARCHICAL,
    })

    if (response && !response.hasOwnProperty('error')) {
      yield put({
        type: SUCCESS_FETCH_HIERARCHICAL_SITE_GROUPS,
        payload: response,
      })
    } else {
      yield put({ type: FAILURE_FETCH_HIERARCHICAL_SITE_GROUPS })
    }

    yield put({ type: SET_HIERARCHICAL_SITE_GROUPS_LOADING, payload: false })
  } catch (error) {
    console.error('FETCH HIERARCHICAL SITE GROUPS FAILURE', error)
    yield put({ type: FAILURE_FETCH_HIERARCHICAL_SITE_GROUPS })
    yield put({ type: SET_HIERARCHICAL_SITE_GROUPS_LOADING, payload: false })
  }
}

function* jobsTriggersSaga() {
  yield takeLatest(FETCH_TRIGGER_INPUTS, fetchTriggerInputs)
  yield takeLatest(FETCH_TRIGGER_EXISTS, fetchTriggerExists)
  yield takeLatest(CREATE_OR_UPDATE_TRIGGER_RULE, createOrUpdateTriggerRule)
  yield takeLatest(FETCH_TRIGGER_TABLE_DATA, fetchTriggerTableData)
  yield takeLatest(
    FETCH_TRIGGER_DEFAULT_ASSIGNMENTS,
    fetchTriggerDefaultAssignments,
  )
  yield takeLatest(DELETE_TRIGGER_RULE, deleteTriggerRule)
  yield takeLatest(TOGGLE_TRIGGER_RULE, toggleTriggerRule)
  yield takeLatest(DUPLICATE_TRIGGER_RULE, duplicateTriggerRule)
  yield takeLatest(FETCH_TRIGGER_RULE, fetchTriggerRule)
  yield takeLatest(FETCH_HIERARCHICAL_SITE_GROUPS, fetchHierarchicalSiteGroups)
}

export default jobsTriggersSaga
